import { type BigDecimal, createBigDecimal, plus } from '@0xtorch/big-decimal'
import type {
  NftAcquiredTimestamps,
  Transaction,
  TransactionTransfer,
} from '../../types/transaction'

const fiveYearsToAdd = 5

/**
 * PL 項目が未設定の transfer に対してゼロ埋め済みの値を返します
 * @param transfer 対象 transfer
 * @returns PL 項目をゼロ補完した値
 */
export const getNormalizedTransferPl = ({
  transfer,
}: {
  readonly transfer: TransactionTransfer
}): {
  readonly tradePl: BigDecimal
  readonly separateTradePl: BigDecimal
  readonly nftTradePl: BigDecimal
  readonly nftCapitalPl: BigDecimal
  readonly feePl: BigDecimal
  readonly incomePl: BigDecimal
  readonly separateRealizedPl: BigDecimal
  readonly realizedPl: BigDecimal
  readonly airdropPl: BigDecimal
} => ({
  tradePl: transfer.tradePl,
  separateTradePl: transfer.separateTradePl ?? createBigDecimal(0n),
  nftTradePl: transfer.nftTradePl ?? createBigDecimal(0n),
  nftCapitalPl: transfer.nftCapitalPl ?? createBigDecimal(0n),
  feePl: transfer.feePl,
  incomePl: transfer.incomePl,
  separateRealizedPl: transfer.separateRealizedPl ?? createBigDecimal(0n),
  realizedPl: transfer.realizedPl ?? createBigDecimal(0n),
  airdropPl: transfer.airdropPl ?? createBigDecimal(0n),
})

/**
 * 仕訳・集計用に trade 系 PL の合計値を返します
 * @param transfer 対象 transfer
 * @returns trade 系 PL 合計
 */
export const getTotalTradePl = ({
  transfer,
}: {
  readonly transfer: TransactionTransfer
}): BigDecimal => {
  const normalized = getNormalizedTransferPl({ transfer })
  return plus(
    plus(normalized.tradePl, normalized.separateTradePl),
    plus(normalized.nftTradePl, normalized.nftCapitalPl),
  )
}

/**
 * transfer の総 PL を追加項目込みで計算します
 * @param transfer 対象 transfer
 * @returns 総 PL
 */
export const getTotalPl = ({
  transfer,
}: {
  readonly transfer: TransactionTransfer
}): BigDecimal => {
  const normalized = getNormalizedTransferPl({ transfer })
  return plus(
    plus(
      plus(normalized.feePl, normalized.incomePl),
      plus(normalized.realizedPl, normalized.airdropPl),
    ),
    plus(
      plus(normalized.separateRealizedPl, normalized.tradePl),
      plus(
        normalized.separateTradePl,
        plus(normalized.nftTradePl, normalized.nftCapitalPl),
      ),
    ),
  )
}

/**
 * NFT 売却時の trade PL を保有期間に応じて分類します
 * @param tradePl 分類前の trade PL
 * @param transfer 対象 transfer
 * @param timestamp 売却日時
 * @param nftAcquiredTimestamps NFT 取得日時マップ
 * @returns 分類後の trade PL 項目
 */
export const splitTradePlByNftHoldingPeriod = ({
  tradePl,
  transfer,
  timestamp,
  nftAcquiredTimestamps,
}: {
  readonly tradePl: BigDecimal
  readonly transfer: TransactionTransfer
  readonly timestamp: number
  readonly nftAcquiredTimestamps?: NftAcquiredTimestamps
}): {
  readonly tradePl: BigDecimal
  readonly nftTradePl: BigDecimal
  readonly nftCapitalPl: BigDecimal
} => {
  if (transfer.asset.type !== 'Nft') {
    return {
      tradePl,
      nftTradePl: createBigDecimal(0n),
      nftCapitalPl: createBigDecimal(0n),
    }
  }

  const acquiredAt = nftAcquiredTimestamps?.get(transfer.asset.id)
  if (acquiredAt === undefined) {
    return {
      tradePl: createBigDecimal(0n),
      nftTradePl: tradePl,
      nftCapitalPl: createBigDecimal(0n),
    }
  }

  const acquiredAtDate = new Date(acquiredAt)
  const capitalThreshold = new Date(acquiredAtDate.getTime())
  capitalThreshold.setUTCFullYear(
    capitalThreshold.getUTCFullYear() + fiveYearsToAdd,
  )

  if (timestamp < capitalThreshold.getTime()) {
    return {
      tradePl: createBigDecimal(0n),
      nftTradePl: tradePl,
      nftCapitalPl: createBigDecimal(0n),
    }
  }

  return {
    tradePl: createBigDecimal(0n),
    nftTradePl: createBigDecimal(0n),
    nftCapitalPl: tradePl,
  }
}

/**
 * transaction の in transfer に含まれる NFT の取得日時を更新します
 * @param transaction 対象 transaction
 * @param nftAcquiredTimestamps NFT 取得日時マップ
 * @returns 更新後の NFT 取得日時マップ
 */
export const updateNftAcquiredTimestampsByTransaction = ({
  transaction,
  nftAcquiredTimestamps,
}: {
  readonly transaction: Transaction
  readonly nftAcquiredTimestamps?: NftAcquiredTimestamps
}): NftAcquiredTimestamps => {
  const nextNftAcquiredTimestamps =
    nftAcquiredTimestamps ?? new Map<string, number>()
  for (const transfer of transaction.ins) {
    if (transfer.asset.type !== 'Nft') {
      continue
    }
    nextNftAcquiredTimestamps.set(transfer.asset.id, transaction.timestamp)
  }
  return nextNftAcquiredTimestamps
}
